import { cn } from '@/utils/cn';

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-muted',
        className
      )}
    />
  );
}

export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn('bg-background rounded-xl border border-border p-6', className)}>
      <Skeleton className="h-32 w-full rounded-lg mb-4" />
      <Skeleton className="h-4 w-3/4 mb-2" />
      <Skeleton className="h-4 w-1/2 mb-4" />
      <Skeleton className="h-8 w-full rounded-lg" />
    </div>
  );
}

export function SkeletonDoctorCard() {
  return (
    <div className="bg-background rounded-2xl p-6 border border-border flex flex-col items-center">
      <Skeleton className="w-28 h-28 rounded-full mb-4" />
      <Skeleton className="h-5 w-32 mb-2" />
      <Skeleton className="h-4 w-24 mb-2" />
      <Skeleton className="h-3 w-20 mb-3" />
      <div className="flex gap-1 mb-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="w-4 h-4 rounded" />
        ))}
      </div>
      <Skeleton className="h-3 w-full mb-2" />
      <Skeleton className="h-3 w-3/4 mb-4" />
      <Skeleton className="h-10 w-full rounded-xl" />
    </div>
  );
}

export function SkeletonSpecialtyCard() {
  return (
    <div className="bg-background rounded-xl border border-border p-6 h-[160px] flex flex-col items-center justify-center">
      <Skeleton className="w-14 h-14 rounded-full mb-4" />
      <Skeleton className="h-4 w-24 mb-2" />
      <Skeleton className="h-3 w-16 mb-2" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

export function SkeletonTestimonialCard() {
  return (
    <div className="bg-background rounded-2xl p-6 md:p-8">
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="w-5 h-5 rounded" />
        ))}
      </div>
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-3/4 mb-6 pt-4 border-t" />
      <div className="flex items-center gap-3">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div>
          <Skeleton className="h-4 w-24 mb-1" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonFAQItem() {
  return (
    <div className="py-5 border-b border-border">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="w-5 h-5 rounded-full" />
      </div>
    </div>
  );
}

export function SkeletonTrustBarItem() {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <Skeleton className="w-10 h-10 rounded-full" />
      <div>
        <Skeleton className="h-4 w-16 mb-1" />
        <Skeleton className="h-3 w-12" />
      </div>
    </div>
  );
}

export function SkeletonTableRow() {
  return (
    <div className="flex items-center gap-4 p-4 border-b border-border">
      <Skeleton className="w-10 h-10 rounded" />
      <Skeleton className="h-4 w-1/4" />
      <Skeleton className="h-4 w-1/6" />
      <Skeleton className="h-4 w-1/6" />
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  );
}

export function SkeletonListItem() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton className="w-12 h-12 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-8 w-16 rounded-lg" />
    </div>
  );
}
